import { AnalyticsUtils } from '@analytics/utils';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  handleNavigationAction,
  handlePrivateErrorAction,
} from '@innogy/feh/shared';
import {
  isEditorActive,
  type LinkField,
} from '@sitecore-jss/sitecore-jss-angular';
import { filter, map, mergeMap, of } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';

import { selectHomeBatteryFunnelSettings } from './homebattery-funnel.selectors';
import {
  handleHomeBatteryFunnelGenericErrorAction,
  handleHomeBatteryFunnelPrivateErrorAction,
  initializeHomeBatteryFunnelStepAction,
  mintHomeBatteryTrackingIdAction,
  navigateToOrderFormPageAction,
  navigateToQuestionairePageAction,
  navigateToStartPageAction,
  onHomeBatteryFunnelNavigationAction,
  setHomeBatteryFunnelInitializedAction,
} from './homebattery-funnel.actions';
import { initializeQuestionnaire } from '../questionnaire/homebattery-questionnaire.actions';
import { openGenericModal } from '@innogy/common-ui-modals';

@Injectable()
export class HomeBatteryFunnelEffects {
  private readonly actions$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly analyticsUtils = inject(AnalyticsUtils);

  private readonly funnelSettings$ = this.store$.select(
    selectHomeBatteryFunnelSettings
  );
  private readonly isXpEditorActive$ = () => of(isEditorActive());

  public readonly onInitHomeBatteryFunnel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initializeHomeBatteryFunnelStepAction),
      concatLatestFrom(() => [this.funnelSettings$, this.isXpEditorActive$()]),
      filter(
        ([action, funnelSettings, isXpEditorActive]) =>
          !isXpEditorActive &&
          (!funnelSettings.initialized ||
            action.funnelSettings?.id !== funnelSettings.id)
      ),
      map(([{ step, funnelSettings }]) => {
        if (!funnelSettings) {
          return handleHomeBatteryFunnelPrivateErrorAction({
            message: `FunnelSettings are not defined on ${step}`,
          });
        } else if (!funnelSettings['startPage']) {
          return handleHomeBatteryFunnelPrivateErrorAction({
            message: `${String('startPage')} is not defined on FunnelSettings`,
          });
        } else if (step !== 'wizard') {
          return onHomeBatteryFunnelNavigationAction({
            page: funnelSettings['startPage'] as unknown as LinkField,
          });
        }

        return setHomeBatteryFunnelInitializedAction({ funnelSettings });
      })
    )
  );

  public readonly onHomeBatteryFunnelNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onHomeBatteryFunnelNavigationAction),
      map(handleNavigationAction)
    )
  );

  public readonly onHomeBatteryFunnelPrivateError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(handleHomeBatteryFunnelPrivateErrorAction),
      map(handlePrivateErrorAction)
    )
  );

  public readonly onHomeBatteryFunnelGenericError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(handleHomeBatteryFunnelGenericErrorAction),
      concatLatestFrom(() => this.funnelSettings$),
      map(([_, funnelSettings]) => {
        if (!funnelSettings.genericErrorModal) {
          return handleHomeBatteryFunnelPrivateErrorAction({
            message: 'genericErrorModal is not defined on FunnelSettings',
          });
        }
        return openGenericModal(funnelSettings.genericErrorModal);
      })
    )
  );

  public readonly mintHomeBatteryTrackingId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setHomeBatteryFunnelInitializedAction),
      map(() =>
        mintHomeBatteryTrackingIdAction({
          id: this.analyticsUtils.generateAnalyticsE2EId(),
        })
      )
    )
  );

  public readonly handlePageNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        navigateToOrderFormPageAction,
        navigateToQuestionairePageAction,
        navigateToStartPageAction
      ),
      map(({ pageData: page, type }) => {
        if (page?.href) {
          return onHomeBatteryFunnelNavigationAction({
            page,
          });
        }
        return handleHomeBatteryFunnelPrivateErrorAction({
          message: `${type} - No page found for this routing action in funnel settings.`,
        });
      })
    )
  );

  public readonly reinitializeQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initializeHomeBatteryFunnelStepAction),
      concatLatestFrom(() => this.funnelSettings$),
      mergeMap(([params, existingFunnelSettings]) =>
        existingFunnelSettings.initialized
          ? []
          : [
              initializeQuestionnaire({
                useNewFlow: params.funnelSettings?.useNewFlow ?? false,
              }),
            ]
      )
    )
  );
}
